import { storeToRefs } from 'pinia'
import { useUserPreferences } from '~/composables/ui/useUserPreferences'
import {
  mergeOnboardingStateByNewest,
  readOnboardingStateFromStorage,
  writeOnboardingStateToStorage,
} from '~/onboarding/storage'
import type { OnboardingStateMap, OnboardingTourDefinition, OnboardingTourProgressStatus } from '~/onboarding/types'
import { useOnboardingStore } from '~~/stores/onboarding'

/**
 * Centralise la fusion / persistance de l’état de progression des tours d’onboarding
 * entre le store Pinia (mémoire), le localStorage et les préférences utilisateur (API).
 */
export function useOnboardingStatePersistence() {
  const { prefs, isReady: userPreferencesReady, setOnboardingState: setOnboardingStateInPreferences } =
    useUserPreferences()
  const onboardingStore = useOnboardingStore()
  const { stateMap: onboardingState, hasHydrated } = storeToRefs(onboardingStore)

  function persistOnboardingStateMap(nextState: OnboardingStateMap): void {
    writeOnboardingStateToStorage(nextState)
    setOnboardingStateInPreferences(nextState)
  }

  function resolveOnboardingStateMapForChecks(): OnboardingStateMap {
    const fromStore = onboardingStore.stateMap
    const fromLocalStorage = readOnboardingStateFromStorage()
    const fromPrefs = prefs.value.onboarding?.state ?? {}
    const fromPersistence = mergeOnboardingStateByNewest(fromLocalStorage, fromPrefs)
    return mergeOnboardingStateByNewest(fromPersistence, fromStore)
  }

  function syncStateFromStorageAndApi(): void {
    const localState = readOnboardingStateFromStorage()
    const serverState = prefs.value.onboarding?.state ?? {}
    const mergedState = mergeOnboardingStateByNewest(localState, serverState)
    onboardingStore.setStateMap(mergedState)
    writeOnboardingStateToStorage(mergedState)
    if (JSON.stringify(serverState) !== JSON.stringify(mergedState)) {
      setOnboardingStateInPreferences(mergedState)
    }
  }

  function updateTourProgress(
    tour: OnboardingTourDefinition,
    status: OnboardingTourProgressStatus,
    currentStep: number
  ): OnboardingStateMap {
    const nextState: OnboardingStateMap = {
      ...onboardingStore.stateMap,
      [tour.id]: {
        tourId: tour.id,
        version: tour.version,
        status,
        currentStep,
        updatedAt: new Date().toISOString(),
      },
    }
    onboardingStore.setStateMap(nextState)
    return nextState
  }

  function resetSingleTourProgress(tourId: string): void {
    const { [tourId]: _removedTour, ...nextState } = onboardingStore.stateMap
    onboardingStore.setStateMap(nextState)
    persistOnboardingStateMap(nextState)
  }

  function clearAllProgress(): void {
    onboardingStore.setStateMap({})
    writeOnboardingStateToStorage({})
    persistOnboardingStateMap({})
  }

  return {
    onboardingState,
    hasHydrated,
    prefs,
    userPreferencesReady,
    resolveOnboardingStateMapForChecks,
    persistOnboardingStateMap,
    syncStateFromStorageAndApi,
    updateTourProgress,
    resetSingleTourProgress,
    clearAllProgress,
  }
}
